import React, { useState } from "react";
import PropTypes from "prop-types";

import {
	Table,
	TableHead,
	TableBody,
	TableRow,
	TableCell,
	TablePagination,
	TableFooter,
	Checkbox,
	Button
} from "@mui/material";
import { withStyles } from "@mui/styles";
import { CBCheckbox } from "../index";
import classNames from "classnames";
import _ from "lodash";
import { timeConversion } from "client-app-core";
import { Translate, getTranslation, getLocalize } from "orion-components/i18n";
import moment from "moment";
import { useSelector } from "react-redux";

const headStyles = {
	head: {
		color: "#828283",
		border: "none",
		lineHeight: 1,
		padding: "8px 16px",
		whiteSpace: "nowrap"
	},
	headRTL: {
		color: "#828283",
		border: "none",
		lineHeight: 1,
		padding: "8px 16px",
		whiteSpace: "nowrap",
		textAlign: "right"
	},
	paddingCheckbox: {
		padding: "0 0 0 4px"
	},
	paddingCheckboxRLT: {
		padding: "0 4px 0 0"
	},
	tableCellRoot: {
		padding: 8
	}
};

const headPropTypes = {
	classes: PropTypes.object.isRequired,
	columns: PropTypes.array.isRequired,
	removing: PropTypes.bool,
	hasAttachments: PropTypes.bool,
	dir: PropTypes.string
};

const headDefaultProps = {
	removing: false,
	hasAttachments: false
};

let CBTableHead = ({ classes, columns, removing, hasAttachments, dir }) => {
	const tableHeadStyles = {
		tableCell: {
			...(dir === "rtl" && { textAlign: "right" })
		}
	};
	return (
		<TableHead data-testid="orion-components-cbcomponents-cbtable-cbtable-table-head">
			<TableRow data-testid="orion-components-cbcomponents-cbtable-cbtable-table-row">
				{removing && (
					<TableCell
						className={classes.head}
						variant="head"
						padding="none"
						style={tableHeadStyles.tableCell} data-testid="orion-components-cbcomponents-cbtable-cbtable-table-cell"
					/>
				)}
				{_.map(columns, (column) => (
					<TableCell
						variant="head"
						key={column.id}
						classes={{
							head: dir === "rtl" ? classes.headRTL : classes.head,
							paddingCheckbox: dir === "rtl" ? classes.paddingCheckboxRLT : classes.paddingCheckbox
						}}
						padding={column.type === "checkbox" ? "checkbox" : "normal"} data-testid="orion-components-cbcomponents-cbtable-cbtable-table-cell-2"
					>
						{column.name}
					</TableCell>
				))}
				{hasAttachments && (
					<TableCell
						variant="head"
						id="attachments"
						classes={{
							head: dir === "rtl" ? classes.headRTL : classes.head
						}}
						padding="normal" data-testid="orion-components-cbcomponents-cbtable-cbtable-table-cell-3"
					>
						<Translate value="global.CBComponents.CBTable.attachments" />
					</TableCell>
				)}
			</TableRow>
		</TableHead>
	);
};

CBTableHead.propTypes = headPropTypes;
CBTableHead.defaultProps = headDefaultProps;

CBTableHead = withStyles(headStyles)(CBTableHead);

const tableRowStyles = {
	body: {
		color: "#FFFFFF",
		padding: "8px 16px",
		whiteSpace: "nowrap"
	},
	bodyRTL: {
		color: "#FFFFFF",
		padding: "8px 16px",
		whiteSpace: "nowrap",
		textAlign: "right"
	},
	root: {
		color: "#828283",
		"&$checked": {
			color: "#35b7f3"
		}
	},
	checked: {},
	text: {
		textTransform: "none",
		color: "#35b7f3",
		padding: 0,
		textAlign: "left",
		"&:hover": {
			backgroundColor: "transparent"
		}
	},
	label: {
		justifyContent: "flex-start"
	},
	textRTL: {
		textTransform: "none",
		color: "#35b7f3",
		padding: 0,
		textAlign: "right",
		"&:hover": {
			backgroundColor: "transparent"
		}
	},
	paddingCheckbox: {
		padding: "0 0 0 4px"
	},
	paddingCheckboxRTL: {
		padding: "0 4px 0 0"
	}
};

const tableRowPropTypes = {
	canViewNotes: PropTypes.bool,
	classes: PropTypes.object.isRequired,
	columns: PropTypes.array.isRequired,
	row: PropTypes.object.isRequired,
	removing: PropTypes.bool,
	selectKey: PropTypes.string,
	selectAction: PropTypes.func,
	setNowAction: PropTypes.func,
	handleCheckboxToggle: PropTypes.func,
	handleLookupQuery: PropTypes.func,
	toggleRemove: PropTypes.func,
	removals: PropTypes.array,
	attachmentAction: PropTypes.func,
	hasAttachments: PropTypes.bool,
	disableCheckbox: PropTypes.bool,
	timeFormatPreference: PropTypes.string,
	locale: PropTypes.string,
	dir: PropTypes.string
};

const tableRowDefaultProps = {
	removing: false,
	selectKey: "",
	selectAction: () => { },
	handleCheckboxToggle: () => { },
	toggleRemove: () => { },
	removals: [],
	attachmentAction: () => { },
	hasAttachments: false,
	disableCheckbox: false,
	timeFormatPreference: "12-hour",
	locale: "en"
};

let CBTableRow = ({
	canViewNotes,
	classes,
	columns,
	row,
	removing,
	selectKey,
	selectAction,
	setNowAction,
	handleCheckboxToggle,
	toggleRemove,
	removals,
	attachmentAction,
	hasAttachments,
	disableCheckbox,
	timeFormatPreference,
	dir,
	locale
}) => {
	const CBTableRowStyles = {
		tableCell: {
			padding: 0,
			width: 42,
			...(dir === "rtl" && { textAlign: "right" })
		},
		textAlignRight: {
			...(dir === "rtl" && { textAlign: "right" })
		}
	};

	const convertTimeFormat = (value) => {
		if (timeFormatPreference === "12-hour") {
			const dateTime = getLocalize(
				`${value.split(" ")[0]} ${value.split(" ")[1]} ${value.split(" ")[2]}`,
				"global.date.full"
			) + value.split(" ")[3];
			return dateTime;
		} else {
			const dateTime = getLocalize(
				`${value.split(" ")[0]} ${value.split(" ")[1]}`,
				"global.date.full24-hour"
			) + value.split(" ")[2];
			return dateTime;
		}
	};

	return (
		<TableRow data-testid="orion-components-cbcomponents-cbtable-cbtable-table-row-2">
			{removing && (
				<TableCell className={classes.body} padding="none" style={CBTableRowStyles.tableCell} data-testid="orion-components-cbcomponents-cbtable-cbtable-table-cell-4">
					<Checkbox
						color="primary"
						classes={{
							root: classes.root,
							checked: classes.checked
						}}
						checked={_.includes(removals, row.order)}
						onChange={() => toggleRemove(row.order)} data-testid="orion-components-cbcomponents-cbtable-cbtable-checkbox"
					/>
				</TableCell>
			)}
			{_.map(columns, (column) => {
				const selectable = column[selectKey];
				// TODO: Add a time display config or a format prop on column
				let value = null;
				let userName = null;
				let dateTime = null;
				switch (column.type) {
					case "date-time":
						if (!row.data[column.id]) value = "";
						else if (column.includeTime) {
							value = timeConversion.convertToUserTime(
								moment(row.data[column.id]).locale(locale),
								`full_${timeFormatPreference}`
							);

							value = convertTimeFormat(value);
						} else if (!column.includeTime) {
							value = timeConversion.convertToUserTime(
								moment(row.data[column.id]).locale(locale),
								"MM/DD/YYYY"
							);
							value = getLocalize(value, "global.date.mid");
						}
						break;
					case "choice":
						{
							const selectedOption = _.find(
								column.options,
								(option) => option.id === row.data[column.id]
							);
							value = selectedOption ? selectedOption.value : "";
						}
						break;
					case "notes":
						value = row.data[column.id] || "";
						break;
					case "lookup":
						value = row.data[column.id] ? row.data[column.id].name : "";
						break;
					default:
						value = row.data[column.id];
						break;
				}

				const tableCellStyle = {
					textAlign: column.type === "checkbox" ? "center" : dir && dir === "rtl" ? "right" : undefined
				};

				if (column.required) {
					userName = row.data["checklist_user-completed"];
					dateTime = timeConversion.convertToUserTime(moment(row.data["checklist_show-time"]).locale(locale), `full_${timeFormatPreference}`);
					dateTime = convertTimeFormat(dateTime);
				}
				return (
					<TableCell
						variant="body"
						color="primary"
						classes={{
							body: dir === "rtl" ? classes.bodyRTL : classes.body,
							paddingCheckbox: dir === "rtl" ? classes.paddingCheckboxRTL : classes.paddingCheckbox
						}}
						key={column.id}
						padding={_.isBoolean(value) ? "checkbox" : "normal"}
						style={tableCellStyle} data-testid="orion-components-cbcomponents-cbtable-cbtable-table-cell-5"
					>
						{_.isBoolean(value) ? (
							<CBCheckbox
								checked={value}
								handleChange={() => handleCheckboxToggle(row.order, column.id)}
								disableCheckbox={disableCheckbox}
							/>
						) : selectable ? (
							<Button
								onClick={(e) => selectAction(e, row)}
								variant="text"
								className={classNames(
									dir == "rtl" ? classes.textRTL : classes.text,
									dir && classes.label
								)}
								disableFocusRipple={true}
								disableRipple={true} data-testid="orion-components-cbcomponents-cbtable-cbtable-button"
							>
								{value}
							</Button>
						) : column.type === "notes" ? (
							<Button
								onClick={(e) => selectAction(e, row)}
								variant="text"
								disabled={!canViewNotes}
								className={classNames(
									dir == "rtl" ? classes.textRTL : classes.text,
									dir && classes.label
								)}
								disableFocusRipple={true}
								disableRipple={true}
								style={!canViewNotes ? { color: "#828283" } : {}} data-testid="orion-components-cbcomponents-cbtable-cbtable-button-2"
							>
								{getTranslation("global.CBComponents.CBTable.viewNotes")}
							</Button>
						) : column.type === "date-time" && !column.forCheckBox && !value ? (
							<Button
								onClick={() => setNowAction(column, row)}
								variant="text"
								disabled={!canViewNotes}
								className={classNames(
									dir == "rtl" ? classes.textRTL : classes.text,
									dir && classes.label
								)}
								disableFocusRipple={true}
								disableRipple={true}
								style={!canViewNotes ? { color: "#828283" } : {}} data-testid="orion-components-cbcomponents-cbtable-cbtable-button-3"
							>
								{getTranslation("global.CBComponents.CBTable.setNow")}
							</Button>
						) : (
							value
						)}
						{Boolean(_.size(userName)) && Boolean(_.size(dateTime)) && <span style={{ display: "block", fontSize: 10 }}>{userName} | {dateTime}</span>}
					</TableCell>
				);
			})}
			{
				_.size(row.attachments) > 0 ? (
					<TableCell
						variant="body"
						color="primary"
						className={classes.body}
						key="attachments"
						padding="normal"
						style={CBTableRowStyles.textAlignRight} data-testid="orion-components-cbcomponents-cbtable-cbtable-table-cell-6"
					>
						<Button
							onClick={(e) => attachmentAction(e, row)}
							variant="text"
							className={classNames(dir == "rtl" ? classes.textRTL : classes.text, dir && classes.label)}
							disableFocusRipple={true}
							disableRipple={true} data-testid="orion-components-cbcomponents-cbtable-cbtable-button-4"
						>
							Attachments
						</Button>
					</TableCell>
				) : hasAttachments && _.size(row.attachments) === 0 ? (
					<TableCell
						variant="body"
						color="primary"
						className={classes.body}
						key="attachments"
						padding="normal"
						style={CBTableRowStyles.textAlignRight} data-testid="orion-components-cbcomponents-cbtable-cbtable-table-cell-7"
					/>
				) : null
			}
		</TableRow >
	);
};

CBTableRow.propTypes = tableRowPropTypes;
CBTableRow.defaultProps = tableRowDefaultProps;

CBTableRow = withStyles(tableRowStyles)(CBTableRow);

const paginationStyles = {
	root: {
		width: "100%"
	}
};

const paginationPropTypes = {
	classes: PropTypes.object.isRequired,
	count: PropTypes.number.isRequired,
	rowsPerPage: PropTypes.number.isRequired,
	page: PropTypes.number.isRequired,
	handleChangePage: PropTypes.func.isRequired,
	handleChangeRowsPerPage: PropTypes.func.isRequired,
	rowsPerPageOptions: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
	paginationSlotProps: PropTypes.object
};

let CBTablePagination = ({
	classes,
	count,
	rowsPerPage,
	rowsPerPageOptions,
	page,
	handleChangePage,
	handleChangeRowsPerPage,
	paginationSlotProps
}) => {
	return (
		<TablePagination
			className={classNames(classes.root, "cb-table-pagination")}
			count={count}
			rowsPerPage={rowsPerPage}
			page={page}
			onPageChange={handleChangePage}
			onRowsPerPageChange={handleChangeRowsPerPage}
			{...(rowsPerPageOptions && rowsPerPageOptions.length
				? {
					rowsPerPageOptions
				}
				: {})}
			style={{ direction: "ltr" }}
			slotProps={paginationSlotProps} data-testid="orion-components-cbcomponents-cbtable-cbtable-table-pagination"
		/>
	);
};

CBTablePagination.propTypes = paginationPropTypes;

CBTablePagination = withStyles(paginationStyles)(CBTablePagination);

const tableStyles = {
	root: {
		width: "100%"
	},
	body: {
		borderBottom: "1px solid #515151"
	},
	tableWrapper: {
		width: "100%",
		overflowX: "scroll",
		borderLeft: "1px solid #515151",
		borderRight: "1px solid #515151"
	}
};

const tablePropTypes = {
	listPaginationOptions: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
	defaultListPagination: PropTypes.number,
	canViewNotes: PropTypes.bool,
	classes: PropTypes.object.isRequired,
	rows: PropTypes.array,
	columns: PropTypes.array.isRequired,
	handleChange: PropTypes.func,
	removing: PropTypes.bool,
	selectKey: PropTypes.string,
	selectAction: PropTypes.func,
	noPagination: PropTypes.bool,
	setNowAction: PropTypes.func,
	handleCheckboxToggle: PropTypes.func,
	handleLookupQuery: PropTypes.func,
	toggleRemove: PropTypes.func,
	removals: PropTypes.array,
	attachmentAction: PropTypes.func,
	hasAttachments: PropTypes.bool,
	disableCheckbox: PropTypes.bool,
	timeFormatPreference: PropTypes.string,
	dir: PropTypes.string,
	paginationSlotProps: PropTypes.object
};

const tableDefaultProps = {
	rows: [],
	removing: false,
	selectKey: "",
	selectAction: () => { },
	setNowAction: () => { },
	handleCheckboxToggle: () => { },
	toggleRemove: () => { },
	removals: [],
	attachmentAction: () => { },
	hasAttachments: false,
	disableCheckbox: false,
	timeFormatPreference: "12-hour",
	paginationSlotProps: {}
};

const CBTable = ({
	listPaginationOptions,
	defaultListPagination,
	canViewNotes,
	classes,
	rows,
	columns,
	handleChange,
	removing,
	selectKey,
	setNowAction,
	selectAction,
	noPagination,
	handleCheckboxToggle,
	handleLookupQuery,
	toggleRemove,
	removals,
	hasAttachments,
	attachmentAction,
	disableCheckbox,
	timeFormatPreference,
	dir,
	paginationSlotProps
}) => {
	const [page, setPage] = useState(0);
	const [rowsPerPageOptions, setRowsPerPageOptions] = useState(listPaginationOptions);
	const [rowsPerPage, setRowsPerPage] = useState(
		(defaultListPagination || defaultListPagination === 0) &&
			listPaginationOptions &&
			listPaginationOptions.includes(defaultListPagination)
			? defaultListPagination
			: listPaginationOptions && listPaginationOptions.length
				? listPaginationOptions[0]
				: 10
	);
	const locale = useSelector((state) => state.i18n.locale);

	const handleChangePage = (event, page) => {
		setPage(page);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(event.target.value);
	};

	return (
		<div className={classes.tableWrapper}>
			<Table className={classes.root} data-testid="orion-components-cbcomponents-cbtable-cbtable-table">
				<CBTableHead columns={columns} removing={removing} hasAttachments={hasAttachments} dir={dir} />
				{_.size(rows) ? (
					<TableBody data-testid="orion-components-cbcomponents-cbtable-cbtable-table-body">
						{_.map(
							noPagination ? rows : _.slice(rows, page * rowsPerPage, page * rowsPerPage + rowsPerPage),
							(row, index) => (
								<CBTableRow
									key={index}
									columns={columns}
									row={row}
									canViewNotes={canViewNotes}
									handleChange={handleChange}
									handleLookupQuery={handleLookupQuery}
									removing={removing}
									selectKey={selectKey}
									setNowAction={setNowAction}
									selectAction={selectAction}
									handleCheckboxToggle={handleCheckboxToggle}
									toggleRemove={toggleRemove}
									removals={removals}
									attachmentAction={attachmentAction}
									hasAttachments={hasAttachments}
									disableCheckbox={disableCheckbox}
									timeFormatPreference={timeFormatPreference}
									dir={dir}
									locale={locale}
								/>
							)
						)}
					</TableBody>
				) : null}
				{!noPagination && (
					<TableFooter>
						<TableRow data-testid="orion-components-cbcomponents-cbtable-cbtable-table-row-3">
							<CBTablePagination
								count={rows ? rows.length : 0}
								rowsPerPage={rowsPerPage}
								rowsPerPageOptions={rowsPerPageOptions}
								page={page}
								handleChangePage={handleChangePage}
								handleChangeRowsPerPage={handleChangeRowsPerPage}
								paginationSlotProps={paginationSlotProps}
							/>
						</TableRow>
					</TableFooter>
				)}
			</Table>
		</div>
	);
};

CBTable.propTypes = tablePropTypes;
CBTable.defaultProps = tableDefaultProps;

export default withStyles(tableStyles)(CBTable);
